// Mirrors the backend digest_sender.apply_filters so the on-screen preview matches
// the sent email exactly (the WYSIWYG principle). A "view" is a saved filter set.

export interface DigestFilters {
  health?: 'all' | 'red' | 'yellow' | 'green';
  product?: string; // 'all' or a product_type
  direction?: 'all' | 'inbound' | 'outbound';
  meter_type?: 'all' | 'custody' | 'check';
  meters?: string[]; // meter_product_id allowlist (empty = any)
}

export interface DigestView {
  name: string;
  filters: DigestFilters;
  include_regression?: boolean;
}

export interface DigestSchedule {
  freq: 'daily' | 'weekly' | 'monthly';
  weekday?: number; // 0=Mon … 6=Sun (weekly)
  day_of_month?: number; // 1–28 (monthly)
  hour?: number; // 0–23 UTC
}

// A section of the digest = one dashboard component, in the user's chosen order.
export interface DigestSection {
  id: string; // dashboard section id (see DASHBOARD_SECTIONS)
  filters?: DigestFilters;      // meter-control-charts: which meters to chart
  include_regression?: boolean; // meter-control-charts: overlay the regression
}

// The dashboard sections available to add to a digest (matches ModernDashboard).
export const DASHBOARD_SECTIONS: {
  id: string;
  title: string;
  hasFilter?: boolean;
  hasRegression?: boolean;
  note?: string;
}[] = [
  { id: 'meter-fleet-status', title: 'Meter Status Overview' },
  { id: 'meter-health-grid', title: 'Meter Health Monitor' },
  { id: 'meter-control-charts', title: 'Meter Factor Control Charts', hasFilter: true, hasRegression: true },
  { id: 'cost-of-non-compliance', title: 'Cost of Non-Compliance' },
  { id: 'fleet-health-overview', title: 'Master Meter Health Overview' },
];

export const sectionTitle = (id: string) =>
  DASHBOARD_SECTIONS.find((s) => s.id === id)?.title || id;

export interface DigestConfig {
  user_id?: string;
  config_id?: string;
  name: string;
  enabled: boolean;
  recipients: string[];
  schedules: DigestSchedule[];
  sections: DigestSection[];
  views?: DigestView[]; // legacy (pre-sections); ignored going forward
  created_at?: string;
  updated_at?: string;
}

export function applyViewFilters(meters: any[], f: DigestFilters = {}): any[] {
  const health = f.health || 'all';
  const product = f.product || 'all';
  const direction = f.direction || 'all';
  const mtype = f.meter_type || 'all';
  const meterIds = new Set(f.meters || []);

  return meters.filter((m) => {
    if (health !== 'all' && m.current_health !== health) return false;
    if (product !== 'all' && m.product_type !== product) return false;
    if (direction !== 'all' && (m.meter_direction || 'inbound') !== direction) return false;
    if (mtype !== 'all' && (m.meter_type || 'custody') !== mtype) return false;
    if (meterIds.size && !meterIds.has(m.meter_product_id)) return false;
    return true;
  });
}

export function fleetCounts(meters: any[]) {
  const c = { total: meters.length, critical: 0, warning: 0, healthy: 0, drifting: 0 };
  meters.forEach((m) => {
    if (m.current_health === 'red') c.critical += 1;
    else if (m.current_health === 'yellow') c.warning += 1;
    else if (m.current_health === 'green') c.healthy += 1;
    if (m.regression_drift?.drift_flag) c.drifting += 1;
  });
  return c;
}

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function scheduleLabel(s: DigestSchedule): string {
  const hh = `${String(s.hour ?? 13).padStart(2, '0')}:00 UTC`;
  if (s.freq === 'daily') return `Daily · ${hh}`;
  if (s.freq === 'weekly') return `Weekly · ${WEEKDAYS[s.weekday ?? 0]} · ${hh}`;
  if (s.freq === 'monthly') return `Monthly · day ${s.day_of_month ?? 1} · ${hh}`;
  return s.freq;
}
